import React, { useEffect, useState } from 'react';
import pb from '../lib/pocketbase';
import BlockListEditor from './BlockListEditor';
import { useSocieteData } from '../context/SocieteContext';
// DnD Imports
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiEye, FiEyeOff, FiMenu, FiChevronUp, FiChevronDown, FiShield } from 'react-icons/fi';
import './Admin.css';
import '../RecruitmentComponents.css';

const SocieteLoader = () => {
    const [msgIdx, setMsgIdx] = useState(0);
    const messages = ["Analyse de la société...", "Chargement de la structure...", "Synchronisation PocketBase...", "Préparation de l'interface..."];

    useEffect(() => {
        const timer = setInterval(() => {
            setMsgIdx(prev => (prev + 1) % messages.length);
        }, 2200);
        return () => clearInterval(timer);
    }, []);

    return (
        <div className="recrutement-loader-container" style={{ minHeight: '80vh' }}>
            <div className="loader-visual">
                <div className="loader-orbit orbit-1"></div>
                <div className="loader-orbit orbit-2"></div>
                <div className="loader-orbit orbit-3"></div>
                <div className="loader-logo-wrapper">
                    <img src="/images/logo.png" alt="Version Net" className="loader-logo" />
                </div>
            </div>
            <div className="loader-message-wrapper">
                <p className="loader-text" style={{ color: 'var(--admin-text)' }}>{messages[msgIdx]}</p>
                <div className="loader-progress-bar">
                    <div className="loader-progress-fill"></div>
                </div>
            </div>
        </div>
    );
};

const SortableModalCategory = ({ cat, index }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: cat.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        background: 'var(--admin-card-bg)',
        border: '1px solid var(--admin-border)',
        borderRadius: '8px',
        padding: '1rem',
        marginBottom: '0.5rem',
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        cursor: 'grab'
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            <div style={{ color: 'var(--admin-text-muted)' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="9" cy="5" r="1" /><circle cx="9" cy="12" r="1" /><circle cx="9" cy="19" r="1" /><circle cx="15" cy="5" r="1" /><circle cx="15" cy="12" r="1" /><circle cx="15" cy="19" r="1" /></svg>
            </div>
            <div style={{ fontWeight: 600, flex: 1 }}>{cat.title}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', background: 'var(--admin-hover-bg)', padding: '2px 8px', borderRadius: '4px' }}>
                Ordre : {index + 1}
            </div>
        </div>
    );
};

function SortableSection({ sec, selectedSection, onSelect, deleteSection, onTogglePublish }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sec.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1000 : 'auto',
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`section-admin-item ${selectedSection?.id === sec.id ? 'active' : ''} ${sec.is_published === false ? 'is-draft' : ''}`}
            onClick={() => onSelect(sec)}
        >
            <div
                {...attributes}
                {...listeners}
                className="section-drag-handle"
                onClick={e => e.stopPropagation()}
            >
                <FiMenu />
            </div>

            <div className="section-icon-preview">
                <FiShield />
            </div>

            <div className="section-info">
                <div className="section-meta">
                    <span className="section-badge">Page</span>
                    {sec.is_published === false && (
                        <span className="draft-badge"><FiEyeOff /> Dépublié</span>
                    )}
                </div>
                <h4 className="section-name">{sec.title}</h4>
                <p className="section-excerpt">#{sec.slug}</p>
            </div>

            <div className="section-item-actions">
                <button
                    type="button"
                    className={`btn-icon-round ${sec.is_published === false ? 'is-draft-btn' : 'is-published-btn'}`}
                    onClick={(e) => { e.stopPropagation(); onTogglePublish(sec); }}
                    title={sec.is_published === false ? "Publier" : "Dépublier"}
                >
                    {sec.is_published === false ? <FiEyeOff /> : <FiEye />}
                </button>
                <button
                    type="button"
                    className="btn-icon-round"
                    onClick={(e) => { e.stopPropagation(); onSelect(sec); }}
                    title="Modifier"
                >
                    <FiEdit2 />
                </button>
                <button
                    type="button"
                    className="btn-icon-round delete"
                    onClick={(e) => { e.stopPropagation(); deleteSection(sec.id); }}
                    title="Supprimer"
                >
                    <FiTrash2 />
                </button>
            </div>
        </div>
    );
}

function SortableCategory({ cat, sections, selectedCategory, handleSelectCategory, createSection, deleteCategory, selectedSection, handleSelectSection, deleteSection, onSectionDragEnd, sensors, confirmNavigation, toggleSectionStatus }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: cat.id });

    const catSections = sections
        .filter(s => (typeof s.category === 'object' ? s.category?.id : s.category) === cat.id)
        .sort((a, b) => (a.order || 0) - (b.order || 0));

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 900 : 'auto',
    };

    return (
        <div ref={setNodeRef} className="admin-structure-group" style={{
            ...style,
            background: 'var(--admin-hover-bg)',
            border: '1px solid var(--admin-border)',
            borderRadius: '8px',
            padding: '1.25rem',
            marginBottom: '1.5rem'
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div {...attributes} {...listeners} style={{ cursor: 'grab', touchAction: 'none', color: 'var(--admin-text-muted)', display: 'flex' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="9" cy="5" r="1" /><circle cx="9" cy="12" r="1" /><circle cx="9" cy="19" r="1" /><circle cx="15" cy="5" r="1" /><circle cx="15" cy="12" r="1" /><circle cx="15" cy="19" r="1" /></svg>
                    </div>
                    <div>
                        <h3
                            onClick={() => confirmNavigation(() => handleSelectCategory(cat))}
                            style={{ margin: 0, cursor: 'pointer', textDecoration: selectedCategory?.id === cat.id ? 'underline' : 'none', color: 'var(--admin-text)', fontSize: '1.1rem' }}
                            title="Cliquez pour éditer"
                        >
                            📁 {cat.title}
                        </h3>
                        <span style={{ fontSize: '0.8rem', color: 'var(--admin-text-muted)' }}>#{cat.slug}</span>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button type="button" className="btn btn-sm btn-secondary" onClick={() => createSection(cat.id)}>+ Ajouter une section</button>
                    <button type="button" className="btn-icon delete" onClick={() => deleteCategory(cat.id)}>🗑️</button>
                </div>
            </div>

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => onSectionDragEnd(e, cat.id)}>
                <SortableContext items={catSections.map(s => s.id)} strategy={verticalListSortingStrategy}>
                    <div className="structure-list" style={{ display: 'flex', flexDirection: 'column' }}>
                        {catSections.length === 0 && <div className="empty-state-mini" style={{ padding: '1rem', fontStyle: 'italic', color: 'var(--admin-text-muted)', textAlign: 'center', border: '1px dashed var(--admin-border)', borderRadius: '6px' }}>Aucune section dans ce groupe.</div>}

                        {catSections.map((sec) => (
                            <SortableSection
                                key={sec.id}
                                sec={sec}
                                selectedSection={selectedSection}
                                onSelect={(s) => confirmNavigation(() => handleSelectSection(s))}
                                deleteSection={deleteSection}
                                onTogglePublish={toggleSectionStatus}
                            />
                        ))}
                    </div>
                </SortableContext>
            </DndContext>
        </div>
    );
}

export default function AdminSocieteManager() {
    const { categories, sections, hero: contextHero, loading: contextLoading, refresh } = useSocieteData();
    const [selectedSection, setSelectedSection] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isDirty, setIsDirty] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const onCategoryDragEnd = async (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = categories.findIndex(c => c.id === active.id);
        const newIndex = categories.findIndex(c => c.id === over.id);
        const newCategories = arrayMove(categories, oldIndex, newIndex);
        try {
            await Promise.all(newCategories.map((c, idx) => pb.collection('societe_categories').update(c.id, { order: idx })));
            refresh();
        } catch (err) { console.error(err); }
    };

    const onSectionDragEnd = async (event, catId) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const catSections = sections.filter(s => (typeof s.category === 'object' ? s.category?.id : s.category) === catId).sort((a, b) => (a.order || 0) - (b.order || 0));
        const oldIndex = catSections.findIndex(s => s.id === active.id);
        const newIndex = catSections.findIndex(s => s.id === over.id);
        const newCatSections = arrayMove(catSections, oldIndex, newIndex);
        try {
            await Promise.all(newCatSections.map((s, idx) => pb.collection('societe_sections').update(s.id, { order: idx })));
            refresh();
        } catch (err) { console.error(err); }
    };

    const toggleSectionStatus = async (sec) => {
        try {
            await pb.collection('societe_sections').update(sec.id, { is_published: !sec.is_published });
            showToast(sec.is_published ? "Mise hors-ligne effectuée" : "Mise en ligne effectuée");
            await refresh();
        } catch (err) {
            console.error("Error toggling section status", err);
            showToast("Erreur lors du changement de statut", "error");
        }
    };

    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [showSaveConfirm, setShowSaveConfirm] = useState(false);
    const [pendingSave, setPendingSave] = useState(null);
    const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null, isDanger: false });
    const [showNewCatModal, setShowNewCatModal] = useState(false);
    const [showManageCatsModal, setShowManageCatsModal] = useState(false);
    const [newCatTitle, setNewCatTitle] = useState('');

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
    };

    const [editData, setEditData] = useState({ title: '', slug: '', content: '', blocks: [], category: '', order: 0, is_published: true });
    const [catEditData, setCatEditData] = useState({ title: '', slug: '', order: 0 });

    const slugify = (text) => text.toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');


    useEffect(() => {
        const handleBeforeUnload = (e) => { if (isDirty) { e.preventDefault(); e.returnValue = ''; } };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDirty]);

    const [heroData, setHeroData] = useState({
        title: 'Société',
        subtitle: 'Qui sommes-nous ?',
        slider_title: '',
        slider_subtitle: '',
        buttons: []
    });
    const [heroId, setHeroId] = useState(null);

    // Initialize Hero Data from Context
    useEffect(() => {
        if (contextHero) {
            setHeroData({
                title: contextHero.title || 'Société',
                subtitle: contextHero.subtitle || '',
                slider_title: contextHero.slider_title || '',
                slider_subtitle: contextHero.slider_subtitle || '',
                buttons: contextHero.buttons || []
            });
            // Fetch ID silently for updates
            pb.collection('societe_hero').getFullList({ sort: '-created', max: 1 }).then(list => {
                if (list.length > 0) setHeroId(list[0].id);
            });
        }
    }, [contextHero]);

    const saveHero = (e) => { e.preventDefault(); setPendingSave(() => executeSaveHero); setShowSaveConfirm(true); };
    const executeSaveHero = async () => {
        setIsSaving(true);
        try {
            if (heroId) { await pb.collection('societe_hero').update(heroId, heroData); }
            else { const newHero = await pb.collection('societe_hero').create(heroData); setHeroId(newHero.id); }
            showToast("En-tête mis à jour !");
        } catch (err) { console.error(err); showToast("Erreur sauvegarde.", "error"); }
        finally { setIsSaving(false); setIsDirty(false); setShowSaveConfirm(false); }
    };

    const updateButton = (index, field, value) => {
        const newButtons = [...heroData.buttons];
        newButtons[index][field] = value;
        setHeroData({ ...heroData, buttons: newButtons });
        setIsDirty(true);
    };
    const addButton = () => { setHeroData({ ...heroData, buttons: [...heroData.buttons, { label: 'Bouton', url: '/', style: 'btn-secondary' }] }); setIsDirty(true); };
    const removeButton = (index) => { setHeroData({ ...heroData, buttons: heroData.buttons.filter((_, i) => i !== index) }); setIsDirty(true); };

    const handleSelectSection = (section) => {
        setSelectedSection(section); setSelectedCategory(null);
        setEditData({ title: section.title, slug: section.slug, content: section.content, category: (typeof section.category === 'object' ? section.category?.id : section.category) || '', order: section.order || 0, blocks: section.blocks || [], is_published: section.is_published !== false });
        setIsDirty(false);
        setTimeout(() => document.getElementById('admin-editor-area')?.scrollIntoView({ behavior: 'smooth' }), 100);
    };

    const confirmNavigation = (nextAction) => {
        if (isDirty) {
            setConfirmModal({
                show: true,
                title: "Modifications non enregistrées",
                message: "Ignorer les changements ?",
                isDanger: true,
                onConfirm: () => {
                    if (contextHero) {
                        setHeroData({
                            title: contextHero.title || 'Société',
                            subtitle: contextHero.subtitle || '',
                            slider_title: contextHero.slider_title || '',
                            slider_subtitle: contextHero.slider_subtitle || '',
                            buttons: contextHero.buttons || []
                        });
                    }
                    setIsDirty(false);
                    setConfirmModal(prev => ({ ...prev, show: false }));
                    nextAction();
                }
            });
        } else {
            nextAction();
        }
    };

    const handleSelectCategory = (category) => {
        setSelectedCategory(category); setSelectedSection(null);
        setCatEditData({ title: category.title, slug: category.slug, order: category.order || 0 });
        setIsDirty(false);
        setTimeout(() => document.getElementById('admin-editor-area')?.scrollIntoView({ behavior: 'smooth' }), 100);
    };

    const createCategory = () => { setNewCatTitle(''); setShowNewCatModal(true); };
    const handleConfirmCreateCategory = async () => {
        if (!newCatTitle.trim()) return;
        const maxOrder = categories.reduce((max, c) => Math.max(max, c.order || 0), 0);
        const slug = slugify(newCatTitle);
        try {
            setIsSaving(true);
            const newCat = await pb.collection('societe_categories').create({ title: newCatTitle, slug, order: maxOrder + 1 });
            await refresh(); handleSelectCategory(newCat); showToast("Groupe créé !"); setShowNewCatModal(false);
        } catch (err) { console.error(err); showToast("Erreur création groupe", "error"); }
        finally { setIsSaving(false); }
    };

    const deleteCategory = async (id) => {
        setConfirmModal({
            show: true, title: "Supprimer ?", message: "Supprimer ce groupe et TOUTES ses sections (cascade) ?", isDanger: true, onConfirm: async () => {
                try {
                    setIsSaving(true);
                    // 1. Delete sections belonging to this category
                    const relatedSections = sections.filter(s =>
                        (typeof s.category === 'object' ? s.category?.id : s.category) === id
                    );
                    if (relatedSections.length > 0) {
                        await Promise.all(relatedSections.map(s => pb.collection('societe_sections').delete(s.id)));
                    }

                    // 2. Delete the category itself
                    await pb.collection('societe_categories').delete(id);

                    if (selectedCategory?.id === id) setSelectedCategory(null);
                    await refresh();
                    showToast("Groupe et sections supprimés !");
                }
                catch (err) {
                    console.error("Error deleting category:", err);
                    showToast("Erreur lors de la suppression (vérifiez les droits API)", "error");
                } finally {
                    setIsSaving(false);
                    setConfirmModal(prev => ({ ...prev, show: false }));
                }
            }
        });
    };

    const saveCategory = (e) => { e.preventDefault(); setPendingSave(() => executeSaveCategory); setShowSaveConfirm(true); };
    const executeSaveCategory = async () => {
        if (!selectedCategory) return;
        setIsSaving(true);
        try { await pb.collection('societe_categories').update(selectedCategory.id, catEditData); await refresh(); showToast("Mis à jour !"); }
        catch (err) { console.error(err); } finally { setIsSaving(false); setIsDirty(false); setShowSaveConfirm(false); }
    };

    const createSection = async (categoryId) => {
        const cat = categories.find(c => c.id === categoryId);
        setConfirmModal({
            show: true, title: "Créer une nouvelle page ?", message: `Ajouter dans "${cat?.title}" ?`, isDanger: false, onConfirm: async () => {
                const catSections = sections.filter(s => (typeof s.category === 'object' ? s.category?.id : s.category) === categoryId);
                const maxOrder = catSections.reduce((max, s) => Math.max(max, s.order || 0), 0);
                try {
                    setIsSaving(true);
                    const newSec = await pb.collection('societe_sections').create({ title: 'Nouvelle Page', slug: `page-${Date.now()}`, category: categoryId, content: '<p>Contenu...</p>', order: maxOrder + 1, is_published: true });
                    await refresh(); handleSelectSection(newSec); showToast("Créée !");
                } catch (err) { console.error(err); } finally { setIsSaving(false); setConfirmModal(prev => ({ ...prev, show: false })); }
            }
        });
    };

    const deleteSection = async (id) => {
        setConfirmModal({
            show: true, title: "Supprimer ?", message: "Définitivement ?", isDanger: true, onConfirm: async () => {
                try {
                    setIsSaving(true);
                    await pb.collection('societe_sections').delete(id);
                    if (selectedSection?.id === id) setSelectedSection(null);
                    await refresh();
                    showToast("Page supprimée");
                }
                catch (err) {
                    console.error("Error deleting section:", err);
                    showToast("Erreur lors de la suppression de la page", "error");
                } finally {
                    setIsSaving(false);
                    setConfirmModal(prev => ({ ...prev, show: false }));
                }
            }
        });
    };

    const saveSection = (e) => { e.preventDefault(); setPendingSave(() => executeSaveSection); setShowSaveConfirm(true); };
    const executeSaveSection = async () => {
        if (!selectedSection) return;
        setIsSaving(true);
        try { await pb.collection('societe_sections').update(selectedSection.id, editData); await refresh(); showToast("Sauvegardé !"); }
        catch (err) { console.error(err); } finally { setIsSaving(false); setIsDirty(false); setShowSaveConfirm(false); }
    };

    const cancelChanges = (target) => {
        setConfirmModal({
            show: true,
            title: "Annuler les modifications ?",
            message: "Êtes-vous sûr de vouloir ignorer tous vos changements non enregistrés ? Cette action est irréversible.",
            isDanger: true,
            onConfirm: () => {
                if (target === 'hero') {
                    if (contextHero) {
                        setHeroData({
                            title: contextHero.title || 'Société',
                            subtitle: contextHero.subtitle || '',
                            slider_title: contextHero.slider_title || '',
                            slider_subtitle: contextHero.slider_subtitle || '',
                            buttons: contextHero.buttons || []
                        });
                    }
                    setIsDirty(false);
                } else if (target === 'section' && selectedSection) {
                    handleSelectSection(selectedSection);
                } else if (target === 'category' && selectedCategory) {
                    handleSelectCategory(selectedCategory);
                }
                setConfirmModal(prev => ({ ...prev, show: false }));
            }
        });
    };

    if (contextLoading) return <SocieteLoader />;

    return (
        <section className="section">
            <div className="section-inner">
                <div className="section-header"><h1 className="admin-title-gradient">Gestion de la Société</h1></div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                    <div className="card" style={{ borderLeft: '4px solid #10b981' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', borderBottom: '1px solid var(--border-subtle)', paddingBottom: '0.5rem' }}>
                            <h3 style={{ margin: 0 }}>0. En-tête de la page (Hero)</h3>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                {isDirty && (
                                    <>
                                        <button className="btn btn-cancel" onClick={() => cancelChanges('hero')}>↺ Annuler les modifications</button>
                                        <span className="dirty-badge">● Modifié</span>
                                    </>
                                )}
                                <button className={`btn btn-sm btn-primary ${isDirty ? 'btn-save-dirty' : ''}`} onClick={saveHero} disabled={isSaving}>Enregistrer l'en-tête</button>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(250px, 1fr) 1fr', gap: '1rem', marginBottom: '1rem' }}>
                            <div className="form-group">
                                <label style={{ color: 'var(--primary)' }}>Titre SLIDER (Haut de page)</label>
                                <input type="text" value={heroData.slider_title || ''} onChange={e => { setHeroData({ ...heroData, slider_title: e.target.value }); setIsDirty(true); }} placeholder="ex: Société" />
                            </div>
                            <div className="form-group">
                                <label style={{ color: 'var(--primary)' }}>Sous-titre SLIDER</label>
                                <textarea rows="2" value={heroData.slider_subtitle || ''} onChange={e => { setHeroData({ ...heroData, slider_subtitle: e.target.value }); setIsDirty(true); }} placeholder="ex: Qui sommes-nous ?" />
                            </div>

                            <div className="form-group" style={{ borderTop: '1px dashed var(--border-subtle)', paddingTop: '1rem' }}>
                                <label>Titre CONTENU (Corps de page)</label>
                                <input type="text" value={heroData.title} onChange={e => { setHeroData({ ...heroData, title: e.target.value }); setIsDirty(true); }} />
                            </div>
                            <div className="form-group" style={{ borderTop: '1px dashed var(--border-subtle)', paddingTop: '1rem' }}>
                                <label>Sous-titre CONTENU</label>
                                <textarea rows="2" value={heroData.subtitle} onChange={e => { setHeroData({ ...heroData, subtitle: e.target.value }); setIsDirty(true); }} />
                            </div>
                        </div>

                        <div className="form-group">
                            <label style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                                <span>Boutons d'action</span>
                                <button type="button" className="btn btn-xs btn-outline" onClick={addButton}>+ Ajouter un bouton</button>
                            </label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                {heroData.buttons.length === 0 && <span style={{ color: 'var(--text-muted)', fontStyle: 'italic', fontSize: '0.9rem' }}>Aucun bouton.</span>}
                                {heroData.buttons.map((btn, idx) => (
                                    <div key={idx} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                        <input placeholder="Libellé" value={btn.label} onChange={e => updateButton(idx, 'label', e.target.value)} style={{ flex: 1 }} />
                                        <input placeholder="Lien (URL)" value={btn.url} onChange={e => updateButton(idx, 'url', e.target.value)} style={{ flex: 1 }} />
                                        <select value={btn.style} onChange={e => updateButton(idx, 'style', e.target.value)} style={{ width: '150px' }}>
                                            <option value="btn-secondary">Blanc (Secondaire)</option>
                                            <option value="btn-primary">Vert (Primaire)</option>
                                        </select>
                                        <button type="button" className="btn-icon delete" onClick={() => removeButton(idx)} title="Supprimer">🗑️</button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="card">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <div className="header-title-group">
                                <h3 style={{ margin: 0 }}>1. Structure du Sommaire</h3>
                                <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--admin-text-muted)' }}>Organisez les pages de présentation de la société.</p>
                            </div>
                            <div className="actions-group">
                                <button type="button" className="btn btn-sm btn-outline" onClick={() => setShowManageCatsModal(true)}>📂 Gérer les catégories</button>
                                <button type="button" className="btn btn-sm btn-primary" onClick={createCategory}>+ Ajouter une catégorie</button>
                            </div>
                        </div>
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onCategoryDragEnd}>
                            <SortableContext items={categories.map(c => c.id)} strategy={verticalListSortingStrategy}>
                                {categories.map(cat => (
                                    <SortableCategory
                                        key={cat.id}
                                        cat={cat}
                                        sections={sections}
                                        selectedCategory={selectedCategory}
                                        handleSelectCategory={handleSelectCategory}
                                        createSection={createSection}
                                        deleteCategory={deleteCategory}
                                        selectedSection={selectedSection}
                                        handleSelectSection={handleSelectSection}
                                        deleteSection={deleteSection}
                                        onSectionDragEnd={onSectionDragEnd}
                                        sensors={sensors}
                                        confirmNavigation={confirmNavigation}
                                        toggleSectionStatus={toggleSectionStatus}
                                    />
                                ))}
                            </SortableContext>
                        </DndContext>
                    </div>
                    <div id="admin-editor-area">
                        {selectedSection && (
                            <form onSubmit={saveSection} className="card" style={{ borderTop: '4px solid #10b981' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}><h3>⚙️ Édition Page</h3><button type="submit" className="btn btn-primary">Enregistrer</button></div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                                    <div className="form-group"><label>Titre</label><input type="text" value={editData.title} onChange={e => { setEditData({ ...editData, title: e.target.value, slug: slugify(e.target.value) }); setIsDirty(true); }} /></div>
                                    <div className="form-group"><label>Slug</label><input type="text" value={editData.slug} onChange={e => { setEditData({ ...editData, slug: e.target.value }); setIsDirty(true); }} /></div>
                                    <div className="form-group" style={{ display: 'flex', alignItems: 'center', paddingTop: '1.5rem' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                            <input type="checkbox" checked={editData.is_published} onChange={e => { setEditData({ ...editData, is_published: e.target.checked }); setIsDirty(true); }} />
                                            <span>Publié</span>
                                        </label>
                                    </div>
                                </div>
                                <BlockListEditor blocks={editData.blocks} onChange={b => { setEditData({ ...editData, blocks: b }); setIsDirty(true); }} />
                            </form>
                        )}
                        {selectedCategory && (
                            <form onSubmit={saveCategory} className="card">
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}><h3>📁 Édition Catégorie</h3><button type="submit" className="btn btn-secondary">Enregistrer</button></div>
                                <div className="form-group"><label>Titre</label><input type="text" value={catEditData.title} onChange={e => { setCatEditData({ ...catEditData, title: e.target.value, slug: slugify(e.target.value) }); setIsDirty(true); }} /></div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
            {toast.show && <div className={`admin-toast ${toast.type}`}>{toast.message}</div>}
            {showNewCatModal && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal scale-in" style={{ maxWidth: '450px' }}>
                        <div className="admin-modal-header">
                            <h3 style={{ color: 'var(--admin-primary)' }}>📁 Nouvelle catégorie</h3>
                        </div>
                        <div className="admin-modal-body">
                            <div className="admin-input-group">
                                <label>Titre de la catégorie</label>
                                <input
                                    type="text"
                                    value={newCatTitle}
                                    onChange={e => setNewCatTitle(e.target.value)}
                                    placeholder="Ex: Qui sommes-nous ?"
                                    autoFocus
                                    onKeyDown={e => e.key === 'Enter' && handleConfirmCreateCategory()}
                                    style={{ width: '100%', padding: '0.8rem', borderRadius: '8px', border: '1px solid var(--admin-border)', background: 'var(--admin-input-bg)', color: 'var(--admin-text)' }}
                                />
                            </div>
                        </div>
                        <div className="admin-modal-footer">
                            <button type="button" className="btn btn-ghost" onClick={() => setShowNewCatModal(false)} disabled={isSaving}>Annuler</button>
                            <button type="button" className="btn btn-primary" onClick={handleConfirmCreateCategory} disabled={!newCatTitle.trim() || isSaving}>
                                {isSaving ? (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <svg className="spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" width="16" height="16" style={{ animation: 'spin 1s linear infinite' }}>
                                            <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                                            <path d="M12 2C6.47715 2 2 6.47715 2 12" />
                                        </svg>
                                        Création en cours...
                                    </span>
                                ) : 'Créer la catégorie'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {showManageCatsModal && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal scale-in" style={{ maxWidth: '600px' }}>
                        <div className="admin-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ color: 'var(--admin-primary)', margin: 0 }}>📂 Réordonner les catégories</h3>
                            <button className="btn btn-sm btn-outline" onClick={() => setShowManageCatsModal(false)}>Fermer</button>
                        </div>
                        <div className="admin-modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                            <p style={{ fontSize: '0.9rem', color: 'var(--admin-text-muted)', marginBottom: '1.5rem' }}>
                                Glissez-déposez les catégories pour changer leur ordre d'apparition.
                            </p>
                            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onCategoryDragEnd}>
                                <SortableContext items={categories.map(c => c.id)} strategy={verticalListSortingStrategy}>
                                    <div>
                                        {categories.map((cat, idx) => (
                                            <SortableModalCategory key={cat.id} cat={cat} index={idx} />
                                        ))}
                                    </div>
                                </SortableContext>
                            </DndContext>
                        </div>
                        <div className="admin-modal-footer">
                            <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => setShowManageCatsModal(false)}>Terminer</button>
                        </div>
                    </div>
                </div>
            )}
            {/* GENERIC CONFIRMATION MODAL (Delete/Create) */}
            {confirmModal.show && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal" style={{ maxWidth: '450px' }}>
                        <div className="admin-modal-header">
                            <h3 style={{ color: confirmModal.isDanger ? '#ef4444' : 'var(--admin-primary)' }}>
                                {confirmModal.isDanger ? '⚠️' : '✨'} {confirmModal.title}
                            </h3>
                        </div>
                        <div className="admin-modal-body">
                            <p>{confirmModal.message}</p>
                        </div>
                        <div className="admin-modal-footer">
                            <button className="btn btn-ghost" onClick={() => setConfirmModal(prev => ({ ...prev, show: false }))} disabled={isSaving}>Annuler</button>
                            <button className={`btn ${confirmModal.isDanger ? 'btn-danger' : 'btn-primary'}`} onClick={confirmModal.onConfirm} disabled={isSaving}>
                                {isSaving ? (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <svg className="spinner" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{ animation: 'spin 1s linear infinite' }}>
                                            <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                                            <path d="M12 2C6.47715 2 2 6.47715 2 12" />
                                        </svg>
                                        Action en cours...
                                    </span>
                                ) : 'Confirmer'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* SAVE CONFIRMATION MODAL */}
            {showSaveConfirm && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal" style={{ maxWidth: '400px' }}>
                        <div className="admin-modal-header">
                            <h3 style={{ color: 'var(--admin-primary)' }}>💾 Confirmer l'enregistrement ?</h3>
                        </div>
                        <div className="admin-modal-body">
                            <p>Êtes-vous sûr de vouloir sauvegarder les modifications apportées ?</p>
                        </div>
                        <div className="admin-modal-footer">
                            <button className="btn btn-ghost" onClick={() => { setShowSaveConfirm(false); setPendingSave(null); }} disabled={isSaving}>Annuler</button>
                            <button className="btn btn-primary" onClick={() => pendingSave?.()} disabled={isSaving}>
                                {isSaving ? (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <svg className="spinner" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{ animation: 'spin 1s linear infinite' }}>
                                            <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                                            <path d="M12 2C6.47715 2 2 6.47715 2 12" />
                                        </svg>
                                        Enregistrement...
                                    </span>
                                ) : 'Confirmer et Enregistrer'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}
